import React, { useMemo } from 'react';
import {
  Box,
  Chip,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

function SortableBlock({ block, index, total, onToggle, onLabel, onMove }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: block.block_key });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : block.is_visible ? 1 : 0.55,
  };
  return (
    <Box
      ref={setNodeRef}
      style={style}
      sx={{
        mb: 1.25,
        p: 1.5,
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
      }}
    >
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <IconButton size="small" {...attributes} {...listeners} sx={{ cursor: 'grab' }}>
          <DragIndicatorIcon fontSize="small" />
        </IconButton>
        <Chip size="small" label={`${index + 1}`} sx={{ height: 22 }} />
        <TextField
          size="small"
          label="Block"
          value={block.label_en || ''}
          onChange={(e) => onLabel(block.block_key, e.target.value.slice(0, 120))}
          sx={{ flex: 1 }}
        />
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={block.is_visible !== false}
              onChange={() => onToggle(block.block_key)}
            />
          }
          label={block.is_visible !== false ? 'On' : 'Off'}
          sx={{ m: 0 }}
        />
        <IconButton size="small" disabled={index === 0} onClick={() => onMove(block.block_key, -1)}>
          <ArrowUpwardIcon fontSize="small" />
        </IconButton>
        <IconButton
          size="small"
          disabled={index >= total - 1}
          onClick={() => onMove(block.block_key, 1)}
        >
          <ArrowDownwardIcon fontSize="small" />
        </IconButton>
      </Stack>
      <Typography variant="caption" color="text.secondary" ml={5}>
        <code>{block.block_key}</code>
      </Typography>
    </Box>
  );
}

export default function CitizenFullScreenBoard({ blocks, onChange }) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
  );
  const ordered = useMemo(
    () => [...(blocks || [])].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)),
    [blocks],
  );
  const renumber = (list) => list.map((b, i) => ({ ...b, sort_order: i * 10 }));
  const update = (next) => onChange(renumber(next));

  return (
    <Box
      sx={{
        maxWidth: 480,
        mx: 'auto',
        p: 2,
        borderRadius: 3,
        border: '2px solid',
        borderColor: 'grey.300',
        bgcolor: 'grey.50',
      }}
    >
      <Typography variant="subtitle2" fontWeight={800} mb={1}>
        Full screen · Citizen Home
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block" mb={2}>
        Reorder / hide search vs home feed. Feed carousels stay on the Home carousels tab.
      </Typography>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={(event) => {
          const { active, over } = event;
          if (!over || active.id === over.id) return;
          const oldIndex = ordered.findIndex((b) => b.block_key === active.id);
          const newIndex = ordered.findIndex((b) => b.block_key === over.id);
          if (oldIndex < 0 || newIndex < 0) return;
          update(arrayMove(ordered, oldIndex, newIndex));
        }}
      >
        <SortableContext
          items={ordered.map((b) => b.block_key)}
          strategy={verticalListSortingStrategy}
        >
          {ordered.map((block, index) => (
            <SortableBlock
              key={block.block_key}
              block={block}
              index={index}
              total={ordered.length}
              onToggle={(key) =>
                update(
                  ordered.map((b) =>
                    b.block_key === key ? { ...b, is_visible: !b.is_visible } : b,
                  ),
                )
              }
              onLabel={(key, label_en) =>
                update(
                  ordered.map((b) => (b.block_key === key ? { ...b, label_en } : b)),
                )
              }
              onMove={(key, delta) => {
                const idx = ordered.findIndex((b) => b.block_key === key);
                const next = idx + delta;
                if (idx < 0 || next < 0 || next >= ordered.length) return;
                update(arrayMove(ordered, idx, next));
              }}
            />
          ))}
        </SortableContext>
      </DndContext>
    </Box>
  );
}
